/**
 * Query key conventions, DESIGN.md section 6. Every `useQuery` in the app builds its key
 * here so the /changes poller can invalidate precisely. Inventing a key elsewhere means the
 * poller cannot reach it and the screen goes stale without anyone noticing.
 *
 * A metric key always carries the metric id in position 1, so invalidating one metric never
 * blows away a whole screen.
 */
import type { WindowKind } from '@elutide/shared';

/** Stable hash of a filter object so a key is a value, not an identity. */
export function hashFilters(filters: unknown): string {
  if (filters === undefined || filters === null) return 'none';
  const canonical = JSON.stringify(filters, (_key, value: unknown) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b));
      return Object.fromEntries(entries);
    }
    return value;
  });
  return canonical ?? 'none';
}

export const qk = {
  metric: (metricId: string, window: WindowKind | string = '30d', dims: unknown = null) =>
    ['metric', metricId, window, hashFilters(dims)] as const,
  metrics: (screenId: string, window: WindowKind | string = '30d') =>
    ['metrics', screenId, window] as const,
  metricSeries: (metricId: string, window: WindowKind | string, dims: unknown = null) =>
    ['metric', metricId, window, hashFilters(dims), 'series'] as const,
  metricDefinition: (metricId: string) => ['metric', metricId, 'definition'] as const,

  orders: (queue: string, filters: unknown = null, cursor: string | null = null) =>
    ['orders', queue, hashFilters(filters), cursor] as const,
  orderQueues: () => ['orders', 'queues'] as const,
  order: (orderId: string | number) => ['order', String(orderId)] as const,
  orderTimeline: (orderId: string | number) => ['order', String(orderId), 'timeline'] as const,

  stock: (filters: unknown = null, cursor: string | null = null) =>
    ['stock', hashFilters(filters), cursor] as const,
  stockLedger: (productId: string | number, cursor: string | null = null) =>
    ['stock', String(productId), 'ledger', cursor] as const,

  customers: (filters: unknown = null, cursor: string | null = null) =>
    ['customers', hashFilters(filters), cursor] as const,
  customer: (customerKey: string) => ['customer', customerKey] as const,

  support: (queue: string, filters: unknown = null, cursor: string | null = null) =>
    ['support', queue, hashFilters(filters), cursor] as const,
  ticket: (ref: string) => ['ticket', ref] as const,

  inbox: (filters: unknown = null) => ['inbox', hashFilters(filters)] as const,
  inboxItem: (itemId: string) => ['inbox', 'item', itemId] as const,

  health: () => ['health'] as const,
  healthDetail: () => ['health', 'detail'] as const,
  jobs: () => ['jobs'] as const,
  jobRuns: (name: string) => ['jobs', name, 'runs'] as const,

  session: () => ['session'] as const,
  /** `GET /auth/mode`, anonymous. Constant per deployment, so it is cached for minutes. */
  authMode: () => ['auth', 'mode'] as const,
  settings: (section: string) => ['settings', section] as const,
  users: (cursor: string | null = null) => ['users', cursor] as const,
  userDevices: (userId: string) => ['users', userId, 'devices'] as const,
  userPasskeys: (userId: string) => ['users', userId, 'passkeys'] as const,
  userSessions: (userId: string) => ['users', userId, 'sessions'] as const,
  audit: (filters: unknown = null, cursor: string | null = null) =>
    ['audit', hashFilters(filters), cursor] as const,
  changelog: (cursor: string | null = null) => ['settings', 'changelog', cursor] as const,

  changesCursor: () => ['changes', 'cursor'] as const,

  /** The packing bench. Products carry the current lots; jobs carry the agent's last poll. */
  benchProducts: () => ['bench', 'vial', 'products'] as const,
  benchJobs: () => ['bench', 'vial', 'jobs'] as const,
} as const;

/** The prefixes the /changes poller invalidates, one per entity family. */
export const invalidationRoots = {
  orders: ['orders'] as const,
  order: (id: string) => ['order', id] as const,
  stock: ['stock'] as const,
  customers: ['customers'] as const,
  customer: (key: string) => ['customer', key] as const,
  support: ['support'] as const,
  ticket: (ref: string) => ['ticket', ref] as const,
  inbox: ['inbox'] as const,
  health: ['health'] as const,
  jobs: ['jobs'] as const,
  metric: (id: string) => ['metric', id] as const,
} as const;
